import { lazy, Suspense, useMemo, useState } from "react";
import useSWR, { mutate as globalMutate } from "swr";
import {
  IconAlertTriangle,
  IconBuildingBank,
  IconCheck,
  IconCreditCard,
  IconPlus,
} from "@tabler/icons-react";
import { cfApi, cfFetcher, apiClient } from "../../api/apiClient";

const UploadZone = lazy(() =>
  import("@/components/transactions/UploadZone").then((m) => ({
    default: m.UploadZone,
  })),
);

/**
 * Which months of transactions each bank/card account actually has.
 *
 * The point is the gaps: a bookkeeper needs to see "April is missing" and
 * "June only goes to the 21st" without opening a single file.
 */

type MonthStatus = "complete" | "partial" | "missing";

type CoverageMonth = {
  month: string; // YYYY-MM
  status: MonthStatus;
  transactionCount: number;
  coveredThroughDay: number | null;
};

type CoverageAccount = {
  id: string;
  label: string;
  institution: string | null;
  mask: string | null;
  accountType: "bank" | "credit_card";
  currency: string;
  transactionCount: number;
  firstDate: string;
  lastDate: string;
  uploadedAt: string | null;
  months: CoverageMonth[];
  gapCount: number;
  /** True when statements can be uploaded straight into this account. */
  isLinkedAccount?: boolean;
};

const COVERAGE_KEY = cfApi("/transactions/coverage");

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function monthLabel(monthKey: string): string {
  const [, month] = monthKey.split("-");
  return MONTH_NAMES[Number(month) - 1] ?? monthKey;
}

function monthLabelWithYear(monthKey: string): string {
  const [year, month] = monthKey.split("-");
  return `${MONTH_NAMES[Number(month) - 1] ?? month} ${year}`;
}

function ordinal(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`;
  const suffix = { 1: "st", 2: "nd", 3: "rd" }[day % 10] ?? "th";
  return `${day}${suffix}`;
}

/**
 * "Dec–Mar, May, Jun to the 21st" — consecutive complete months collapse
 * into a range so the line stays readable across a long history.
 */
function buildCoverageSentence(months: CoverageMonth[]): string {
  const parts: string[] = [];
  let runStart: CoverageMonth | null = null;
  let runEnd: CoverageMonth | null = null;

  const flushRun = () => {
    if (!runStart || !runEnd) return;
    parts.push(
      runStart.month === runEnd.month
        ? monthLabel(runStart.month)
        : `${monthLabel(runStart.month)}–${monthLabel(runEnd.month)}`,
    );
    runStart = null;
    runEnd = null;
  };

  for (const month of months) {
    if (month.status === "complete") {
      if (!runStart) runStart = month;
      runEnd = month;
      continue;
    }
    flushRun();
    if (month.status === "partial" && month.coveredThroughDay) {
      parts.push(
        `${monthLabel(month.month)} to the ${ordinal(month.coveredThroughDay)}`,
      );
    }
  }
  flushRun();
  return parts.join(", ");
}

const STATUS_STYLES: Record<MonthStatus, string> = {
  complete: "bg-emerald-100 text-emerald-800 border-emerald-200",
  partial: "bg-amber-100 text-amber-900 border-amber-200",
  missing:
    "bg-white text-red-600 border-red-300 border-dashed line-through decoration-red-400",
};

function MonthChip({ month }: { month: CoverageMonth }) {
  const title =
    month.status === "missing"
      ? `${monthLabelWithYear(month.month)} — no transactions uploaded`
      : month.status === "partial"
        ? `${monthLabelWithYear(month.month)} — covered to the ${ordinal(
            month.coveredThroughDay ?? 0,
          )} · ${month.transactionCount} transactions`
        : `${monthLabelWithYear(month.month)} — ${month.transactionCount} transactions`;

  return (
    <span
      title={title}
      className={`inline-flex min-w-[2.9rem] shrink-0 items-center justify-center rounded-md border px-1.5 py-1 text-[11px] font-medium ${STATUS_STYLES[month.status]}`}
    >
      {monthLabel(month.month)}
      {month.status === "partial" && month.coveredThroughDay
        ? ` ${month.coveredThroughDay}`
        : ""}
    </span>
  );
}

function AccountRow({ account }: { account: CoverageAccount }) {
  const Icon =
    account.accountType === "credit_card" ? IconCreditCard : IconBuildingBank;
  const sentence = buildCoverageSentence(account.months);
  const missing = account.months.filter((month) => month.status === "missing");
  const detail = [
    account.institution,
    account.mask ? `···· ${account.mask}` : null,
    account.currency,
    `${account.transactionCount.toLocaleString()} transactions`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
            <Icon size={16} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[#111827]">
              {account.label}
            </p>
            <p className="truncate text-xs text-[#6B7280]">{detail}</p>
          </div>
        </div>

        {missing.length > 0 ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-medium text-red-700">
            <IconAlertTriangle size={13} />
            {missing.length === 1
              ? `${monthLabelWithYear(missing[0].month)} missing`
              : `${missing.length} months missing`}
          </span>
        ) : (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
            <IconCheck size={13} />
            No gaps
          </span>
        )}
      </div>

      <p className="mt-3 text-sm text-[#374151]">
        <span className="text-[#6B7280]">Uploaded: </span>
        {sentence || "No complete months yet"}
      </p>

      <div className="mt-2 flex flex-wrap gap-1">
        {account.months.map((month) => (
          <MonthChip key={month.month} month={month} />
        ))}
      </div>

      {/* Uploading from the account row is what ties the statement to this
          account, instead of it arriving as a nameless new one. */}
      {account.isLinkedAccount ? (
        <div className="mt-3 border-t border-[#F3F4F6] pt-3">
          <Suspense
            fallback={
              <span className="text-xs text-[#898781]">Loading upload…</span>
            }
          >
            <UploadZone
              bankAccountId={account.id}
              compact
              label={`Upload statement to ${account.label}`}
            />
          </Suspense>
        </div>
      ) : null}
    </div>
  );
}

function AddAccountForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [institution, setInstitution] = useState("");
  const [mask, setMask] = useState("");
  const [accountType, setAccountType] = useState<"bank" | "credit_card">(
    "bank",
  );
  const [currency, setCurrency] = useState("USD");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.post("/bank-accounts", {
        name: name.trim(),
        institution: institution.trim() || null,
        accountType,
        mask: mask.trim() || null,
        currency,
      });
      setName("");
      setInstitution("");
      setMask("");
      setOpen(false);
      onCreated();
    } catch {
      setError("Could not add the account. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] transition hover:bg-[#FAFAFA]"
      >
        <IconPlus size={14} />
        Add account
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Account name (e.g. Chase Business Checking)"
          aria-label="Account name"
          className="rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
        />
        <input
          value={institution}
          onChange={(event) => setInstitution(event.target.value)}
          placeholder="Institution (e.g. Chase)"
          aria-label="Institution"
          className="rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
        />
        <input
          value={mask}
          onChange={(event) => setMask(event.target.value)}
          placeholder="Last 4 digits"
          aria-label="Last four digits"
          maxLength={8}
          className="rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
        />
        <div className="flex gap-2">
          <select
            value={accountType}
            onChange={(event) =>
              setAccountType(event.target.value as "bank" | "credit_card")
            }
            aria-label="Account type"
            className="flex-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
          >
            <option value="bank">Bank</option>
            <option value="credit_card">Credit card</option>
          </select>
          <select
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
            aria-label="Currency"
            className="w-24 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
          >
            <option value="USD">USD</option>
            <option value="ILS">ILS</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
      </div>
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={saving || !name.trim()}
          className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400"
        >
          {saving ? "Adding…" : "Add account"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function AccountGroup({
  title,
  accounts,
}: {
  title: string;
  accounts: CoverageAccount[];
}) {
  if (accounts.length === 0) return null;
  return (
    <div>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#898781]">
        {title}
      </h3>
      <div className="grid gap-3">
        {accounts.map((account) => (
          <AccountRow key={account.id} account={account} />
        ))}
      </div>
    </div>
  );
}

export function StatementCoverageSection() {
  const { data, isLoading, error } = useSWR<{
    accounts: CoverageAccount[];
    asOf: string;
  }>(COVERAGE_KEY, cfFetcher);

  const { banks, cards, totalGaps } = useMemo(() => {
    const accounts = data?.accounts ?? [];
    return {
      banks: accounts.filter((account) => account.accountType !== "credit_card"),
      cards: accounts.filter((account) => account.accountType === "credit_card"),
      totalGaps: accounts.reduce((sum, account) => sum + account.gapCount, 0),
    };
  }, [data]);

  if (isLoading) {
    return (
      <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5">
        <div className="mb-4 h-6 w-56 animate-pulse rounded bg-[#F3F4F6]" />
        <div className="grid gap-3">
          {[0, 1, 2].map((key) => (
            <div
              key={key}
              className="h-28 animate-pulse rounded-xl bg-[#F3F4F6]"
            />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        role="alert"
        className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] px-5 py-4 text-sm text-[#DC2626]"
      >
        Unable to load statement coverage.
      </section>
    );
  }

  const accounts = data?.accounts ?? [];

  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#0f172b]">
            Statement coverage
          </h2>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            Which months of transactions each account has, and what is missing
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <AddAccountForm onCreated={() => void globalMutate(COVERAGE_KEY)} />
        {accounts.length > 0 ? (
          totalGaps > 0 ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700">
              <IconAlertTriangle size={14} />
              {totalGaps} {totalGaps === 1 ? "month" : "months"} missing across
              accounts
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">
              <IconCheck size={14} />
              Every account is complete
            </span>
          )
        ) : null}
        </div>
      </header>

      {accounts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-6 py-8 text-center">
          <p className="text-sm font-medium text-[#111827]">
            No transaction files yet
          </p>
          <p className="mt-1 text-sm text-[#6B7280]">
            Add an account, then upload its statement — coverage appears here.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <AccountGroup title="Banks" accounts={banks} />
          <AccountGroup title="Credit cards" accounts={cards} />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-[#F3F4F6] pt-3 text-xs text-[#52514E]">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm border border-emerald-200 bg-emerald-100" />
              Full month
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm border border-amber-200 bg-amber-100" />
              Partial — number is the last day covered
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm border border-dashed border-red-300 bg-white" />
              Missing
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
